"use client";

import { Clock, Copy, Facebook, Instagram, Linkedin, Loader, Mail, MapPin, Phone, Send, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-white border border-blue-900/20 rounded-lg text-center shadow-md">
        <p className="font-default text-2xl text-blue-900 mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-blue-900/80 font-default font-medium">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Sunil Singh" className="bg-transparent border-0 border-b border-blue-900/20 px-0 h-11 focus-visible:ring-0 focus-visible:border-blue-600" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-blue-900/80 font-default font-medium">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="sunil@hotelrevbaba.com" className="bg-transparent border-0 border-b border-blue-900/20 px-0 h-11 focus-visible:ring-0 focus-visible:border-blue-600" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-blue-900/80 font-default font-medium">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+917972195929" className="bg-transparent border-0 border-b border-blue-900/20 px-0 h-11 focus-visible:ring-0 focus-visible:border-blue-600" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-blue-900/80 font-default font-medium">
                Service Interest
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value || ""}>
                  <SelectTrigger className="w-full bg-transparent border-0 border-b border-blue-900/20 px-0 h-11 focus-visible:ring-0 focus-visible:border-blue-600">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {["OTA Revenue Management", "Hospitality Tech Implementation", "Revenue Audit", "Other"].map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                        {opt}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-blue-900/80 font-default font-medium">
              Inquiry Type
            </FormLabel>
            <FormControl>
              <ToggleGroup type="single" value={field.value || ""} onValueChange={field.onChange} className="flex flex-wrap gap-2 w-full justify-start">
                {["Consultation", "Partnership", "Speaking", "Other"].map((opt, i) => <ToggleGroupItem key={opt} value={opt} data-index={i} className="rounded-lg border border-blue-900/20 bg-white px-4 py-2 text-sm data-[state=on]:bg-blue-900 data-[state=on]:text-white data-[state=on]:border-blue-900 flex-none">
                    {opt}
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-blue-900/80 font-default font-medium">
              Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your property and revenue goals..." className="min-h-[140px] bg-white border border-blue-900/20 rounded-lg focus-visible:ring-0 focus-visible:border-blue-600" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter_opt_in" render={({
      field
    }) => <FormItem className="flex flex-row items-center gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="border-blue-900/40" />
            </FormControl>
            <FormLabel className="text-sm text-slate-600 font-default font-normal cursor-pointer">
              Subscribe to revenue management insights
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-blue-900 text-white hover:bg-blue-800 font-default font-medium text-base shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : <>
            Book a Consultation
            <Send className="ml-2 size-4" />
          </>}
      </Button>

      <div className="flex items-center gap-2 pt-2">
        <Clock className="size-4 text-blue-900" />
        <p className="text-sm text-slate-600 font-default">
          We respond to all inquiries within 24 business hours.
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const socialLinks = [{
    name: "Facebook",
    icon: Facebook,
    href: "#"
  }, {
    name: "Instagram",
    icon: Instagram,
    href: "#"
  }, {
    name: "Twitter",
    icon: Twitter,
    href: "#"
  }, {
    name: "LinkedIn",
    icon: Linkedin,
    href: "#"
  }];
  const hours = [{
    day: "Mon - Fri",
    time: "9:00 AM - 5:00 PM"
  }, {
    day: "Saturday",
    time: "By Appointment"
  }, {
    day: "Sunday",
    time: "Closed"
  }];
  return <section data-section-id="845" id="revenuejourneyformcontact" className="bg-slate-50 py-20 md:py-28 px-4 sm:px-6 lg:px-8">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7">
            <div className="bg-white border border-blue-900/10 rounded-lg p-8 sm:p-10 lg:p-14 shadow-md">
              <div className="mb-10">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-900 font-default font-medium mb-4">
                  Get in Touch
                </p>
                <h2 className="font-default text-4xl sm:text-5xl text-blue-900 leading-tight mb-4">
                  Maximize Your Revenue.
                </h2>
                <p className="text-slate-600 text-base max-w-md font-default">
                  Ready to optimize your hotel's performance? Contact Sunil Singh today for a professional revenue audit and strategy session.
                </p>
              </div>

              <FormProvider formKey="revenue_journey_form_contact" formId="revenue_journey_form_contact" sectionName="revenuejourneyformcontact" defaultValues={{
              name: "",
              email: "",
              phone: "",
              service_interest: "",
              inquiry_type: "",
              message: "",
              newsletter_opt_in: false
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              }
            }} submitText="Book a Consultation" successMessage="Thank you! Your inquiry has been received. We will contact you shortly." errorMessage="Something went wrong. Please try again or email us directly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="flex flex-col gap-4">
              <div className="bg-white border border-blue-900/10 rounded-lg p-7 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Phone className="size-5 text-blue-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.2em] text-blue-900 font-default font-medium mb-2">
                      Phone
                    </p>
                    <p className="font-default text-2xl text-blue-900 mb-1">
                      +917972195929
                    </p>
                    <Link to="tel:+917972195929" className="text-blue-900 text-sm underline underline-offset-4 decoration-blue-900/40 hover:decoration-blue-900">
                      Tap to call
                    </Link>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-blue-900/10 rounded-lg p-7 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Mail className="size-5 text-blue-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.2em] text-blue-900 font-default font-medium mb-2">
                      Email
                    </p>
                    <p className="font-default text-2xl text-blue-900 mb-1 break-all">
                      info@hotelrevbaba.com
                    </p>
                    <Button variant="ghost" size="sm" className="h-auto p-0 text-blue-900 text-sm hover:bg-transparent underline underline-offset-4 decoration-blue-900/40 hover:decoration-blue-900">
                      <Copy className="size-3 mr-1.5" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-blue-900/10 rounded-lg p-7 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Clock className="size-5 text-blue-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.2em] text-blue-900 font-default font-medium mb-3">
                      Office Hours
                    </p>
                    <Table>
                      <TableBody>
                        {hours.map((h, i) => <TableRow key={h.day} data-index={i} className="border-blue-900/10 hover:bg-transparent">
                            <TableCell className="text-blue-900 text-sm font-default font-medium pl-0 py-2">
                              {h.day}
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm text-right pr-0 py-2 font-default">
                              {h.time}
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-blue-900/10 rounded-lg p-7 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <MapPin className="size-5 text-blue-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.2em] text-blue-900 font-default font-medium mb-2">
                      Office
                    </p>
                    <p className="font-default text-xl text-blue-900 mb-2 leading-snug">
                      Global Revenue Consulting
                    </p>
                    <Link to="#" className="text-blue-900 text-sm underline underline-offset-4 decoration-blue-900/40 hover:decoration-blue-900">
                      View Location
                    </Link>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={social.name} data-index={i} to={social.href} aria-label={social.name} className="size-11 rounded-lg bg-white border border-blue-900/20 flex items-center justify-center text-blue-900 hover:bg-blue-900 hover:text-white hover:border-blue-900 transition-colors">
                      <Icon className="size-4" />
                    </Link>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}